import { apiPost } from '../config/api';

const SPEECHMATICS_URL =
  process.env.REACT_APP_SPEECHMATICS_URL || 'wss://eu.rt.speechmatics.com/v2';
const JWT_TTL_SECONDS = 3600;
const JWT_REFRESH_BUFFER_MS = 5 * 60 * 1000;
/** How often we check whether the idle warm socket is still usable. */
const KEEP_ALIVE_MS = 10_000;
/** Replace warm sockets older than this — browsers can't ping, so age is our health signal. */
const MAX_WARM_AGE_MS = 60_000;
/** Delay before opening a replacement warm socket after stop (frees concurrent slots). */
const REWARM_DELAY_MS = 750;
const RECONNECT_DELAY_MS = 1000;
const QUOTA_RETRY_DELAY_MS = 6000;
const MAX_RECONNECTS = 3;
const BACKGROUND_CLOSE_MS = 60_000;
/** Shared timeout for connect / warm wait / RecognitionStarted. */
const TIMEOUT_MS = 8000;
const START_ATTEMPTS = 3;

function extractFromResults(results) {
  if (!results?.length) {
    return { text: '', speaker: 'UU' };
  }

  let speaker = 'UU';
  const parts = [];

  for (const result of results) {
    const alt = result.alternatives?.[0];
    if (!alt?.content) continue;
    if (alt.speaker) speaker = alt.speaker;
    parts.push(alt.content);
  }

  return { text: parts.join(' ').replace(/\s+/g, ' ').trim(), speaker };
}

/**
 * Speechmatics realtime singleton with a persistent warm WebSocket.
 *
 * States:
 * - idle: no socket
 * - warming: opening socket
 * - warm: socket open (optionally recognition already armed), no mic audio
 * - recording: recognition active, audio flowing
 *
 * Between recordings we EndOfStream, then replace the spent socket with a fresh
 * warm one (Speechmatics allows only one StartRecognition per connection).
 * A socket that has already received StartRecognition is never treated as warm.
 */
class SpeechmaticsService {
  constructor() {
    this.ws = null;
    this.onTranscript = null;
    this.onDiarizedTranscript = null;
    this.onFinal = null;
    this.onError = null;
    this.onConnected = null;
    this.onDisconnected = null;
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.interimSpeaker = 'UU';
    this.utterances = [];
    this.mode = 'default';
    this.intentionalClose = false;
    this.jwtCache = null;
    this.jwtFetchPromise = null;
    this.sampleRate = 16000;
    this.recognitionReady = false;
    /** True after StartRecognition on this socket — must rotate before reuse. */
    this.recognitionUsed = false;
    this.audioQueue = [];
    this.lastAudioSeqNo = 0;
    this.errorNotified = false;

    this.state = 'idle';
    this.keepWarmDesired = false;
    this.reconnectAttempts = 0;
    this.maxReconnects = MAX_RECONNECTS;
    this.keepAliveInterval = null;
    this.backgroundTimer = null;
    this.warmPromise = null;
    this.recognitionReadyPromise = null;
    this.recognitionReadyResolve = null;
    this.recognitionReadyReject = null;
    this.endOfTranscriptPromise = null;
    this.endOfTranscriptResolve = null;
    this.listeners = new Set();
    this.connectionLost = false;
    this.warmedAt = 0;
    this.starting = false;
    this.sessionEpoch = 0;
    this.socketGeneration = 0;
    this.connectLock = Promise.resolve();
    this.pendingWarmTimer = null;
    this.endingRecognition = false;
    this.armPromise = null;
    this.micCaptureActive = false;
  }

  clearPendingWarm() {
    if (this.pendingWarmTimer) {
      window.clearTimeout(this.pendingWarmTimer);
      this.pendingWarmTimer = null;
    }
  }

  withSocketLock(fn) {
    const run = this.connectLock.then(fn, fn);
    this.connectLock = run.catch(() => {});
    return run;
  }

  getState() {
    return this.state;
  }

  isWarmStale() {
    return this.warmedAt > 0 && Date.now() - this.warmedAt > MAX_WARM_AGE_MS;
  }

  isWarm() {
    return (
      this.state === 'warm'
      && this.ws?.readyState === WebSocket.OPEN
      && !this.recognitionUsed
      && !this.isWarmStale()
    );
  }

  isSocketOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  isWarming() {
    return this.state === 'warming';
  }

  isRecording() {
    return this.state === 'recording';
  }

  hasConnectionLost() {
    return this.connectionLost;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener({
      state: this.state,
      connectionLost: this.connectionLost,
    });
    return () => this.listeners.delete(listener);
  }

  emit() {
    const snapshot = {
      state: this.state,
      connectionLost: this.connectionLost,
    };
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch {
        // ignore listener errors
      }
    });
  }

  setState(next) {
    if (this.state === next) return;
    this.state = next;
    this.emit();
  }

  async prefetchJWT() {
    if (this.getCachedJWT()) {
      return this.getCachedJWT();
    }
    if (!this.jwtFetchPromise) {
      this.jwtFetchPromise = this.fetchJWT().finally(() => {
        this.jwtFetchPromise = null;
      });
    }
    return this.jwtFetchPromise;
  }

  getCachedJWT() {
    if (this.jwtCache && Date.now() < this.jwtCache.expiresAt - JWT_REFRESH_BUFFER_MS) {
      return this.jwtCache.token;
    }
    return null;
  }

  async getJWT() {
    const cached = this.getCachedJWT();
    if (cached) return cached;
    return this.prefetchJWT();
  }

  async fetchJWT() {
    const data = await apiPost('/api/speechmatics/token', {});
    this.jwtCache = {
      token: data.token,
      expiresAt: Date.now() + JWT_TTL_SECONDS * 1000,
    };
    return data.token;
  }

  /**
   * Open (or keep) a warm WebSocket without starting recognition.
   */
  async warm() {
    this.keepWarmDesired = true;
    this.clearBackgroundTimer();

    if (this.state === 'recording' || this.starting) return;
    if (this.isWarm()) {
      this.reconnectAttempts = 0;
      this.connectionLost = false;
      this.emit();
      return;
    }
    if (this.warmPromise) return this.warmPromise;

    this.warmPromise = this.withSocketLock(async () => {
      if (this.state === 'recording' || this.starting || !this.keepWarmDesired) return;
      if (this.isWarm()) return;
      await this.openWarmSocket();
    })
      .catch(() => {
        if (!this.starting) this.handleDisconnect();
      })
      .finally(() => {
        this.warmPromise = null;
      });

    return this.warmPromise;
  }

  async openWarmSocket() {
    this.intentionalClose = false;
    this.setState('warming');

    const jwt = await this.prefetchJWT();
    if (!this.keepWarmDesired) {
      this.setState('idle');
      return;
    }

    await this.connectSocket(jwt, { startRecognition: false });

    if (!this.keepWarmDesired) {
      this.closeSocket();
      this.setState('idle');
      return;
    }

    this.reconnectAttempts = 0;
    this.connectionLost = false;
    this.warmedAt = Date.now();
    this.recognitionReady = false;
    this.recognitionUsed = false;
    this.lastAudioSeqNo = 0;
    this.setState('warm');
    this.startKeepAlive();
    this.emit();
  }

  /**
   * Wait until the socket is warm (or fail within timeout).
   */
  async whenWarm(timeoutMs = TIMEOUT_MS) {
    if (this.isWarm()) return;

    // Stale "warm" sockets still report state===warm — force a reconnect path
    // instead of waiting for a timeout while nothing changes.
    if (this.state === 'warm' && this.isWarmStale()) {
      this.closeSocket();
      this.setState('idle');
      this.warmedAt = 0;
    }

    if (this.state !== 'warming') {
      this.warm().catch(() => {});
    }
    if (this.isWarm()) return;

    await new Promise((resolve, reject) => {
      let settled = false;
      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        unsubscribe();
        fn(value);
      };

      const timeoutId = window.setTimeout(() => {
        finish(reject, new Error('Could not connect to transcription service.'));
      }, timeoutMs);

      const unsubscribe = this.subscribe(({ state, connectionLost }) => {
        if (state === 'warm' && this.isWarm()) {
          finish(resolve);
        } else if (connectionLost && state === 'idle') {
          finish(reject, new Error('Could not connect to transcription service.'));
        }
      });
    });
  }

  /** Ensure a usable warm socket — never reuses a spent or stale one. */
  async ensureOpenSocket() {
    if (this.isWarm()) return;

    await this.withSocketLock(async () => {
      if (this.isWarm()) return;
      // Drop dead / spent / aged sockets before opening a replacement.
      if (this.isSocketOpen()) {
        this.closeSocket();
      }
      this.setState('idle');
      this.warmedAt = 0;
      this.recognitionUsed = false;
      this.recognitionReady = false;
      await this.openWarmSocket();
    });

    if (!this.isWarm()) {
      throw new Error('Could not connect to transcription service.');
    }
  }

  async start({
    onTranscript,
    onDiarizedTranscript,
    onFinal,
    onError,
    onConnected,
    onDisconnected,
    sampleRate = 16000,
    mode = 'default',
  }) {
    this.clearPendingWarm();
    this.onTranscript = onTranscript;
    this.onDiarizedTranscript = onDiarizedTranscript;
    this.onFinal = onFinal;
    this.onError = onError;
    this.onConnected = onConnected;
    this.onDisconnected = onDisconnected;
    this.sampleRate = sampleRate;
    this.mode = mode;
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.interimSpeaker = 'UU';
    this.utterances = [];
    this.intentionalClose = false;
    // Do NOT clear audioQueue here — HoldToRecord may already be buffering mic
    // audio while the socket/handshake catches up.
    this.errorNotified = false;
    this.keepWarmDesired = true;
    this.starting = true;
    this.sessionEpoch += 1;
    const epoch = this.sessionEpoch;

    const isStale = () => this.sessionEpoch !== epoch || this.intentionalClose;

    try {
      let lastErr = null;
      for (let attempt = 1; attempt <= START_ATTEMPTS; attempt += 1) {
        if (isStale()) return;
        try {
          await this.ensureOpenSocket();
          if (isStale()) return;

          await this.armRecognition({ forRecording: true });
          if (isStale()) return;
          return;
        } catch (err) {
          lastErr = err;
          if (isStale() || /cancelled/i.test(String(err?.message || ''))) return;

          // Hard recreate only after a failed handshake on this socket.
          this.recognitionReady = false;
          this.recognitionUsed = false;
          this.closeSocket();
          this.setState('idle');
          this.warmedAt = 0;
          if (attempt < START_ATTEMPTS) {
            const detail = `${err?.code || ''} ${err?.message || ''}`;
            const delayMs = /quota/i.test(detail) ? QUOTA_RETRY_DELAY_MS : RECONNECT_DELAY_MS;
            await new Promise((resolve) => {
              window.setTimeout(resolve, delayMs);
            });
          }
        }
      }

      if (isStale() || /cancelled/i.test(String(lastErr?.message || ''))) return;
      this.setState(this.isSocketOpen() ? 'warm' : 'idle');
      this.notifyError(lastErr || new Error('Transcription error'), epoch);
    } finally {
      if (this.sessionEpoch === epoch) {
        this.starting = false;
      }
    }
  }

  /**
   * Send StartRecognition on the open socket and wait for RecognitionStarted.
   */
  async armRecognition({ forRecording = false } = {}) {
    if (this.armPromise) {
      try {
        await this.armPromise;
      } catch {
        // Previous arm failed — fall through and try again on a clean socket.
      }
      if (forRecording && this.recognitionReady && this.isSocketOpen()) {
        this.setState('recording');
        this.stopKeepAlive();
        if (this.onConnected) this.onConnected();
        return;
      }
      if (this.recognitionUsed) {
        throw new Error('Could not connect to transcription service.');
      }
    }

    this.armPromise = this.runArmRecognition({ forRecording })
      .finally(() => {
        this.armPromise = null;
      });
    return this.armPromise;
  }

  async runArmRecognition({ forRecording }) {
    if (!this.isSocketOpen() || this.recognitionUsed) {
      throw new Error('Could not connect to transcription service.');
    }

    this.endingRecognition = false;
    this.recognitionReady = false;
    if (forRecording) {
      this.setState('recording');
      this.stopKeepAlive();
    }

    this.armRecognitionReady();
    this.sendStartRecognition();

    let timeoutId;
    const readyTimeout = new Promise((_, reject) => {
      timeoutId = window.setTimeout(
        () => reject(new Error('Transcription service did not start in time.')),
        TIMEOUT_MS
      );
    });

    try {
      await Promise.race([this.recognitionReadyPromise, readyTimeout]);
    } finally {
      window.clearTimeout(timeoutId);
    }

    if (!forRecording) {
      this.setState('warm');
      this.startKeepAlive();
    } else if (this.onConnected) {
      this.onConnected();
    }
  }

  notifyError(err, epoch = this.sessionEpoch) {
    if (this.errorNotified) return;
    if (epoch !== this.sessionEpoch) return;
    if (this.intentionalClose) return;
    if (!this.onError) return;
    this.errorNotified = true;
    this.onError(err instanceof Error ? err : new Error(String(err || 'Transcription error')));
  }

  armRecognitionReady() {
    this.recognitionReadyPromise = new Promise((resolve, reject) => {
      this.recognitionReadyResolve = resolve;
      this.recognitionReadyReject = reject;
    });
    this.recognitionReadyPromise.catch(() => {});
  }

  failRecognitionReady(err) {
    if (this.recognitionReadyReject) {
      this.recognitionReadyReject(err instanceof Error ? err : new Error(String(err || 'failed')));
    }
    this.recognitionReadyResolve = null;
    this.recognitionReadyReject = null;
  }

  resolveEndOfTranscript() {
    const resolve = this.endOfTranscriptResolve;
    this.endOfTranscriptResolve = null;
    this.endOfTranscriptPromise = null;
    this.endingRecognition = false;
    if (resolve) resolve();
  }

  connectSocket(jwt, { startRecognition }) {
    return new Promise((resolve, reject) => {
      this.closeSocket();

      let settled = false;
      let opened = false;
      const settle = (fn, value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        fn(value);
      };

      const timeoutId = window.setTimeout(() => {
        if (this.ws) {
          this.ws.onopen = null;
          this.ws.onmessage = null;
          this.ws.onerror = null;
          this.ws.onclose = null;
          try {
            this.ws.close();
          } catch {
            // ignore
          }
          this.ws = null;
        }
        this.setState('idle');
        settle(reject, new Error('Could not connect to transcription service.'));
      }, TIMEOUT_MS);

      this.ws = new WebSocket(`${SPEECHMATICS_URL}?jwt=${jwt}`);

      this.ws.onopen = () => {
        opened = true;
        if (startRecognition) {
          this.armRecognitionReady();
          this.sendStartRecognition();
        }
        settle(resolve);
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(JSON.parse(event.data));
      };

      this.ws.onerror = () => {
        settle(reject, new Error('Could not connect to transcription service.'));
      };

      this.ws.onclose = () => {
        const wasRecording = this.state === 'recording';
        const wasReady = this.recognitionReady;
        const partial = this.finalTranscript.trim();
        this.ws = null;
        this.recognitionReady = false;
        this.resolveEndOfTranscript();

        if (!opened) {
          this.setState('idle');
          settle(reject, new Error('Could not connect to transcription service.'));
          return;
        }

        if (!settled) {
          settle(reject, new Error('Could not connect to transcription service.'));
        }

        if (wasRecording && !wasReady && !this.intentionalClose) {
          this.failRecognitionReady(new Error('Transcription connection lost before start.'));
        }

        if (!this.intentionalClose && wasRecording && wasReady) {
          if (this.onDisconnected) this.onDisconnected(partial);
        }

        if (this.intentionalClose && !this.keepWarmDesired) {
          this.setState('idle');
          return;
        }

        if (this.starting && !this.intentionalClose) {
          this.setState('idle');
          return;
        }

        this.handleDisconnect();
      };
    });
  }

  /** @deprecated Prefer warm()+start(); kept for older connect(jwt) callers */
  connect(jwt) {
    return this.connectSocket(jwt, { startRecognition: true }).then(() => {
      this.setState('recording');
      return this.recognitionReadyPromise || Promise.resolve();
    });
  }

  getTranscriptionConfig() {
    const base = {
      language: 'ar_en',
      enable_partials: true,
      max_delay: 2,
      max_delay_mode: 'flexible',
      model: 'enhanced',
      domain: 'medical',
    };

    if (this.mode === 'admission') {
      return {
        ...base,
        diarization: 'speaker',
        speaker_diarization_config: {
          max_speakers: 2,
          prefer_current_speaker: true,
          speaker_sensitivity: 0.6,
        },
      };
    }

    return {
      ...base,
      diarization: 'none',
    };
  }

  sendStartRecognition() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const config = {
      message: 'StartRecognition',
      audio_format: {
        type: 'raw',
        encoding: 'pcm_f32le',
        sample_rate: this.sampleRate,
      },
      transcription_config: this.getTranscriptionConfig(),
    };
    this.ws.send(JSON.stringify(config));
    // This socket can never StartRecognition again — rotate after EndOfStream.
    this.recognitionUsed = true;
  }

  sendEndOfStream() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(
      JSON.stringify({
        message: 'EndOfStream',
        last_seq_no: this.lastAudioSeqNo || 0,
      })
    );
  }

  appendUtterance(speaker, text) {
    if (!text) return;
    const last = this.utterances[this.utterances.length - 1];
    if (last && last.speaker === speaker && last.isFinal) {
      last.text = `${last.text} ${text}`.trim();
      return;
    }
    this.utterances.push({ speaker, text, isFinal: true });
  }

  emitDiarized() {
    if (!this.onDiarizedTranscript) return;
    this.onDiarizedTranscript({
      utterances: [...this.utterances],
      interim: this.interimTranscript
        ? { speaker: this.interimSpeaker, text: this.interimTranscript }
        : null,
      flat: this.getLiveTranscript(),
    });
  }

  handleMessage(data) {
    switch (data.message) {
      case 'RecognitionStarted':
        this.recognitionReady = true;
        this.flushAudioQueue();
        if (this.recognitionReadyResolve) {
          this.recognitionReadyResolve();
          this.recognitionReadyResolve = null;
          this.recognitionReadyReject = null;
        }
        break;

      case 'AudioAdded':
        if (typeof data.seq_no === 'number') {
          this.lastAudioSeqNo = data.seq_no;
        }
        break;

      case 'AddPartialTranscript': {
        if (this.state !== 'recording' || this.endingRecognition) break;
        const fromResults = extractFromResults(data.results);
        this.interimTranscript =
          fromResults.text || data.metadata?.transcript || '';
        this.interimSpeaker = fromResults.speaker || 'UU';

        if (this.onTranscript) {
          this.onTranscript({
            interim: this.interimTranscript,
            final: this.finalTranscript,
          });
        }
        this.emitDiarized();
        break;
      }

      case 'AddTranscript': {
        if (this.state !== 'recording' || this.endingRecognition) break;
        const fromResults = extractFromResults(data.results);
        const text = fromResults.text || data.metadata?.transcript || '';
        const speaker = fromResults.speaker || 'UU';

        if (text) {
          this.finalTranscript += `${text} `;
          if (this.mode === 'admission') {
            this.appendUtterance(speaker, text);
          }
        }

        this.interimTranscript = '';
        this.interimSpeaker = 'UU';

        if (this.onTranscript) {
          this.onTranscript({
            interim: '',
            final: this.finalTranscript,
          });
        }
        this.emitDiarized();
        break;
      }

      case 'EndOfTranscript': {
        const onFinal = this.onFinal;
        const emitFinal = Boolean(onFinal) && !this.endingRecognition;
        this.resolveEndOfTranscript();
        if (emitFinal) {
          onFinal(this.finalTranscript.trim());
        }
        break;
      }

      case 'Error': {
        const err = new Error(data.reason || data.type || 'Speechmatics transcription error');
        if (data.type) err.code = data.type;
        // Handshake failures feed start() retries — never flash UI yet.
        if (!this.recognitionReady || this.endingRecognition || this.starting) {
          this.failRecognitionReady(err);
          this.resolveEndOfTranscript();
          break;
        }
        // Mid-recording server error: drop socket quietly and let the next
        // tap get a fresh warm connection instead of a scary banner.
        this.recognitionReady = false;
        this.recognitionUsed = true;
        this.closeSocket();
        this.setState('idle');
        if (this.keepWarmDesired) {
          this.scheduleWarm(/quota/i.test(`${data.type || ''} ${data.reason || ''}`)
            ? QUOTA_RETRY_DELAY_MS
            : REWARM_DELAY_MS);
        }
        break;
      }

      default:
        break;
    }
  }

  getUtterances() {
    return [...this.utterances];
  }

  /**
   * Mark that the mic is capturing for an upcoming/active recording.
   * Allows sendAudio to queue while the socket is still warming.
   */
  beginMicCapture() {
    this.micCaptureActive = true;
    this.audioQueue = [];
  }

  endMicCapture() {
    this.micCaptureActive = false;
  }

  flushAudioQueue() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    for (const chunk of this.audioQueue) {
      this.ws.send(chunk);
    }
    this.audioQueue = [];
  }

  sendAudio(audioChunk) {
    // Drop only when fully idle with no mic session. During warm/warming/start,
    // queue so early speech is not lost before RecognitionStarted.
    if (this.state === 'idle' && !this.starting && !this.micCaptureActive) return;

    const wsOpen = this.ws && this.ws.readyState === WebSocket.OPEN;

    if (!wsOpen || !this.recognitionReady) {
      this.audioQueue.push(audioChunk);
      if (this.audioQueue.length > 300) {
        this.audioQueue.shift();
      }
      return;
    }

    this.ws.send(audioChunk);
  }

  getLiveTranscript() {
    const parts = [this.finalTranscript.trim(), this.interimTranscript.trim()].filter(Boolean);
    return parts.join(' ').trim();
  }

  /**
   * End recognition and rotate to a fresh warm socket immediately.
   * Hard-close (no EndOfTranscript wait) so a quick re-tap cannot race a
   * deferred close against the next StartRecognition handshake.
   */
  stop() {
    this.endRecognitionSession({ rearm: true });
  }

  /**
   * Soft disconnect = end recognition + re-warm (same as stop).
   * Pass { permanent: true } to fully close (logout / leave app).
   */
  disconnect(options = {}) {
    const permanent = options.permanent === true;
    if (permanent) {
      this.keepWarmDesired = false;
      this.clearPendingWarm();
      this.intentionalClose = true;
      this.starting = false;
      this.sessionEpoch += 1;
      this.socketGeneration += 1;
      this.recognitionReady = false;
      this.recognitionUsed = false;
      this.audioQueue = [];
      this.endingRecognition = false;
      this.failRecognitionReady(new Error('cancelled'));
      this.resolveEndOfTranscript();
      this.stopKeepAlive();
      this.clearRecordingHandlers();
      this.closeSocket();
      this.clearTranscript();
      this.micCaptureActive = false;
      this.setState('idle');
      this.reconnectAttempts = 0;
      this.warmedAt = 0;
      this.emit();
      return;
    }

    this.endRecognitionSession({ rearm: true });
  }

  clearRecordingHandlers() {
    this.onTranscript = null;
    this.onDiarizedTranscript = null;
    this.onFinal = null;
    this.onError = null;
    this.onConnected = null;
    this.onDisconnected = null;
  }

  clearTranscript() {
    this.finalTranscript = '';
    this.interimTranscript = '';
    this.interimSpeaker = 'UU';
    this.utterances = [];
  }

  endRecognitionSession({ rearm }) {
    this.socketGeneration += 1;

    this.starting = false;
    this.micCaptureActive = false;
    this.sessionEpoch += 1;
    this.audioQueue = [];
    // Clear so a quick re-tap cannot reuse the previous session's transcript
    // via getLiveTranscript() before the next start() runs.
    this.clearTranscript();
    this.failRecognitionReady(new Error('cancelled'));
    this.clearRecordingHandlers();
    this.resolveEndOfTranscript();
    this.stopKeepAlive();

    // Best-effort EOS then hard close — never wait (waiting caused rotate-vs-start races).
    if (this.isSocketOpen() && this.recognitionUsed) {
      try {
        this.sendEndOfStream();
      } catch {
        // ignore
      }
    }

    this.closeSocket();
    this.recognitionReady = false;
    this.recognitionUsed = false;
    this.setState('idle');
    this.warmedAt = 0;
    this.intentionalClose = false;

    // Delayed re-warm so Speechmatics can free the concurrent session slot
    // from the socket we just closed. Quick re-taps call start() → ensureOpenSocket()
    // and open their own socket under the lock without waiting for this.
    if (rearm && this.keepWarmDesired) {
      this.scheduleWarm(REWARM_DELAY_MS);
    }
  }

  scheduleWarm(delayMs = REWARM_DELAY_MS) {
    this.clearPendingWarm();
    this.pendingWarmTimer = window.setTimeout(() => {
      this.pendingWarmTimer = null;
      if (this.keepWarmDesired && this.state === 'idle' && !this.starting) {
        this.warm().catch(() => {});
      }
    }, delayMs);
  }

  close() {
    this.keepWarmDesired = false;
    this.clearPendingWarm();
    this.clearBackgroundTimer();
    this.reconnectAttempts = 0;
    this.connectionLost = false;
    this.disconnect({ permanent: true });
  }

  closeSocket() {
    if (this.ws) {
      try {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        if (
          this.ws.readyState === WebSocket.OPEN
          || this.ws.readyState === WebSocket.CONNECTING
        ) {
          this.ws.close();
        }
      } catch {
        // ignore
      }
      this.ws = null;
    }
  }

  handleDisconnect() {
    this.stopKeepAlive();
    this.recognitionReady = false;
    this.recognitionUsed = false;
    this.ws = null;
    this.resolveEndOfTranscript();

    if (!this.keepWarmDesired) {
      this.setState('idle');
      return;
    }

    this.setState('idle');

    if (this.starting) return;

    if (this.reconnectAttempts < this.maxReconnects) {
      this.reconnectAttempts += 1;
      this.connectionLost = false;
      this.emit();
      this.scheduleWarm();
      return;
    }

    this.connectionLost = true;
    this.emit();
  }

  startKeepAlive() {
    this.stopKeepAlive();
    this.keepAliveInterval = setInterval(() => {
      if (!this.keepWarmDesired || this.state === 'recording' || this.starting) return;

      const dead = !this.ws || this.ws.readyState !== WebSocket.OPEN;
      const stale = this.state === 'warm' && this.isWarmStale();
      if (!dead && !stale) return;

      if (this.state === 'warming') return;

      this.recognitionReady = false;
      this.recognitionUsed = false;
      if (this.isSocketOpen()) this.closeSocket();
      this.setState('idle');
      this.warmedAt = 0;
      this.warm().catch(() => {});
    }, KEEP_ALIVE_MS);
  }

  stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  handleAppHidden() {
    this.clearBackgroundTimer();
    this.backgroundTimer = window.setTimeout(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        this.close();
      }
    }, BACKGROUND_CLOSE_MS);
  }

  handleAppVisible() {
    this.clearBackgroundTimer();
    this.keepWarmDesired = true;
    this.reconnectAttempts = 0;
    this.connectionLost = false;
    this.warm().catch(() => {});
  }

  clearBackgroundTimer() {
    if (this.backgroundTimer) {
      window.clearTimeout(this.backgroundTimer);
      this.backgroundTimer = null;
    }
  }
}

const speechmaticsService = new SpeechmaticsService();
export default speechmaticsService;

export function mapSpeakerLabel(speaker, speakerMap) {
  if (speaker === 'UU' || !speaker) return 'Unknown';
  return speakerMap[speaker] || speaker;
}

export const DEFAULT_SPEAKER_MAP = {
  S1: 'Nurse',
  S2: 'Patient',
};
